import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileBarChart, Download, Calendar } from "lucide-react";
import * as XLSX from "xlsx";

import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
type RelatorioCachosData = {
  setorId: string;
  setorNome: string;
  variedadeNome: string;
  mes: string;
  mesLabel: string;
  totalPlanejado: number;
  totalColhido: number;
  totalPerdido: number;
  saldo: number;
};

const COLUNAS_CACHOS_COLHER = [null, "11rem", "10rem", "8rem", "8rem", "8rem", "10rem"];

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_CACHOS_COLHER = { periodo: "este-mes", de: "", ate: "", q: "" };
const PERMITIDOS_CACHOS_COLHER = { periodo: ["este-mes", "proximo-mes", "customizado"] } as const;

export default function RelatorioCachosColherPage() {
  // Get current month start/end
  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  // Get next month start/end
  const primeiroDiaProximoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
  const ultimoDiaProximoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0);

  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_CACHOS_COLHER, PERMITIDOS_CACHOS_COLHER);
  const periodo = filtros.periodo as "este-mes" | "proximo-mes" | "customizado";
  // Busca NO CLIENTE, sobre a lista já agrupada. Os KPIs do topo continuam
  // descrevendo o PERÍODO, não o termo procurado.
  const busca = filtros.q;

  // Nos presets, `de`/`ate` ficam VAZIOS na URL e a janela é derivada — só o
  // período customizado grava datas, e é por isso que `?periodo=proximo-mes`
  // continua valendo no mês que vem em vez de congelar as datas de hoje.
  const janelaPreset = periodo === "proximo-mes"
    ? { inicio: primeiroDiaProximoMes, fim: ultimoDiaProximoMes }
    : { inicio: primeiroDiaMes, fim: ultimoDiaMes };
  const dataInicio = filtros.de || janelaPreset.inicio.toISOString().split('T')[0];
  const dataFim = filtros.ate || janelaPreset.fim.toISOString().split('T')[0];

  /** Preset limpa as datas (a janela volta a ser derivada); customizado congela a atual. */
  const handlePeriodoChange = (value: string) => {
    if (value === "customizado") definir({ periodo: value, de: dataInicio, ate: dataFim });
    else definir({ periodo: value, de: "", ate: "" });
  };

  // Build query string for API call
  const queryString = new URLSearchParams({
    dataInicio,
    dataFim,
  }).toString();

  const { data: dadosRelatorio, isLoading } = useQuery<RelatorioCachosData[]>({
    queryKey: [`/api/relatorios/cachos-colher?${queryString}`],
  });

  // Group data by setor for the table — only rows with saldo > 0
  const dadosAgrupados = useMemo(() => {
    if (!dadosRelatorio) return [];

    const setoresMap = new Map<string, {
      setorId: string;
      setorNome: string;
      variedadeNome: string;
      meses: Array<{
        mes: string;
        mesLabel: string;
        saldo: number;
        totalPlanejado: number;
        totalColhido: number;
        totalPerdido: number;
      }>;
      totalSaldo: number;
    }>();

    dadosRelatorio.filter(item => item.saldo > 0).forEach(item => {
      if (!setoresMap.has(item.setorId)) {
        setoresMap.set(item.setorId, {
          setorId: item.setorId,
          setorNome: item.setorNome,
          variedadeNome: item.variedadeNome,
          meses: [],
          totalSaldo: 0,
        });
      }

      const setor = setoresMap.get(item.setorId)!;
      setor.meses.push({
        mes: item.mes,
        mesLabel: item.mesLabel,
        saldo: item.saldo,
        totalPlanejado: item.totalPlanejado,
        totalColhido: item.totalColhido,
        totalPerdido: item.totalPerdido,
      });
      setor.totalSaldo += item.saldo;
    });

    return Array.from(setoresMap.values()).sort((a, b) =>
      a.setorNome.localeCompare(b.setorNome)
    );
  }, [dadosRelatorio]);

  const visiveis = dadosAgrupados.filter(s2 => combina(busca, s2.setorNome, s2.variedadeNome));

  // Calculate totals from ALL rows (including saldo <= 0) for accurate summary cards
  const totais = useMemo(() => {
    if (!dadosRelatorio) return { saldo: 0, planejado: 0, colhido: 0, perdido: 0 };

    return dadosRelatorio.reduce((acc, item) => ({
      saldo: acc.saldo + item.saldo,
      planejado: acc.planejado + item.totalPlanejado,
      colhido: acc.colhido + item.totalColhido,
      perdido: acc.perdido + item.totalPerdido,
    }), { saldo: 0, planejado: 0, colhido: 0, perdido: 0 });
  }, [dadosRelatorio]);

  // Export to Excel
  const handleExportExcel = () => {
    if (dadosAgrupados.length === 0) return;

    const exportData = dadosRelatorio!.filter(item => item.saldo > 0).map(item => ({
      'Setor': item.setorNome,
      'Variedade': item.variedadeNome,
      'Mês': item.mesLabel,
      'Planejado': item.totalPlanejado,
      'Colhido': item.totalColhido,
      'Perdido': item.totalPerdido,
      'Saldo a Colher': item.saldo,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cachos a Colher');

    const fileName = `cachos-colher-${dataInicio}-${dataFim}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader
        titulo="Cachos a colher"
        descricao="Previsão mensal de colheita por setor - Fitas ativas"
      />

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Setor" testId="input-busca-cachos-colher" />}
      >
        <Label htmlFor="periodo" className="text-xs text-muted-foreground whitespace-nowrap">Período</Label>
        <Select
          value={periodo}
          onValueChange={handlePeriodoChange}
        >
          <SelectTrigger id="periodo" className="w-[200px] shrink-0" data-testid="select-periodo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="este-mes">Este Mês</SelectItem>
            <SelectItem value="proximo-mes">Próximo Mês</SelectItem>
            <SelectItem value="customizado">Período Customizado</SelectItem>
          </SelectContent>
        </Select>

        <Label htmlFor="dataInicio" className="text-xs text-muted-foreground whitespace-nowrap">De</Label>
        <Input
          id="dataInicio"
          type="date"
          value={dataInicio}
          onChange={(e) => definir({ de: e.target.value, periodo: "customizado" })}
          disabled={periodo !== "customizado"}
          className="w-[150px] shrink-0 text-xs"
          data-testid="input-data-inicio"
        />

        <Label htmlFor="dataFim" className="text-xs text-muted-foreground whitespace-nowrap">até</Label>
        <Input
          id="dataFim"
          type="date"
          value={dataFim}
          onChange={(e) => definir({ ate: e.target.value, periodo: "customizado" })}
          disabled={periodo !== "customizado"}
          className="w-[150px] shrink-0 text-xs"
          data-testid="input-data-fim"
        />
      </PageToolbar>

      {/* Summary Cards — quatro StatCard idênticos. Sem `max-w-*` na grade: foi
          um cap desses que fez o KPI de Vendas vazar por cima do vizinho (#171). */}
      {!isLoading && dadosRelatorio && dadosRelatorio.length > 0 && (
        <StatCardGrid>
          <StatCard rotulo="Saldo a colher" valor={totais.saldo.toLocaleString()} testId="total-saldo" />
          <StatCard rotulo="Planejado" valor={totais.planejado.toLocaleString()} testId="total-planejado" />
          <StatCard rotulo="Colhido" valor={totais.colhido.toLocaleString()} testId="total-colhido" />
          <StatCard rotulo="Perdido" valor={totais.perdido.toLocaleString()} testId="total-perdido" />
        </StatCardGrid>
      )}

      {/* Gráfico e tabela vivem no MESMO DataCard — aqui só a tabela. */}
      <DataCard
        titulo="Previsão por setor e mês"
        meta={`${visiveis.length} setor(es)`}
        acao={
          dadosAgrupados.length > 0 ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportExcel}
              data-testid="button-export"
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Exportar</span>
            </Button>
          ) : undefined
        }
        testId="card-tabela"
      >
        {isLoading ? (
          <PageSkeleton variante="tabela" linhas={5} className="border-0 rounded-none" />
        ) : dadosAgrupados.length === 0 ? (
          <EmptyState
            icone={Calendar}
            titulo="Nenhum dado no período selecionado"
            descricao="A previsão sai das fitas ativas — amplie o período acima, ou confira se há mangará cortado no intervalo."
            testId="empty-cachos-colher"
          />
        ) : visiveis.length === 0 ? (
          <NadaEncontrado termo={busca} />
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <DataTable colunas={COLUNAS_CACHOS_COLHER}>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead>Setor</DataTableHead>
                    <DataTableHead>Variedade</DataTableHead>
                    <DataTableHead>Mês</DataTableHead>
                    <DataTableHead num>Planejado</DataTableHead>
                    <DataTableHead num>Colhido</DataTableHead>
                    <DataTableHead num>Perdido</DataTableHead>
                    <DataTableHead num>Saldo a colher</DataTableHead>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {visiveis.map((setor) => (
                    setor.meses.map((mes, idx) => (
                      <DataTableRow
                        key={`${setor.setorId}-${mes.mes}`}
                        data-testid={`row-${setor.setorId}-${mes.mes}`}
                      >
                        {/* O setor é a coluna que responde à pergunta da tela;
                            só a primeira linha do grupo o repete. */}
                        <DataTableCell destaque className="truncate">
                          {idx === 0 ? setor.setorNome : ""}
                        </DataTableCell>
                        <DataTableCell>
                          {/* Variedade é CATEGORIA: outline neutro. */}
                          {idx === 0 && (
                            <Badge variant="outline" className="text-xs font-normal">
                              {setor.variedadeNome}
                            </Badge>
                          )}
                        </DataTableCell>
                        <DataTableCell className="text-muted-foreground">{mes.mesLabel}</DataTableCell>
                        <DataTableNum>{mes.totalPlanejado.toLocaleString()}</DataTableNum>
                        <DataTableNum className="text-sucesso">{mes.totalColhido.toLocaleString()}</DataTableNum>
                        <DataTableNum className="text-destructive">{mes.totalPerdido.toLocaleString()}</DataTableNum>
                        <DataTableNum className="font-semibold">{mes.saldo.toLocaleString()}</DataTableNum>
                      </DataTableRow>
                    ))
                  ))}
                </DataTableBody>
              </DataTable>
            </div>

            {/* Mobile: um bloco por setor — grade estável de 3 colunas, sem
                largura fixa em px (foi assim que Atividades perdeu o nome). */}
            <div className="md:hidden divide-y">
              {visiveis.map((setor) => (
                <div key={setor.setorId} className="p-3 space-y-2" data-testid={`card-setor-${setor.setorId}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{setor.setorNome}</span>
                    <Badge variant="outline" className="text-xs font-normal shrink-0">
                      {setor.variedadeNome}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total a colher: <span className="font-mono tabular-nums font-semibold text-foreground">{setor.totalSaldo.toLocaleString()}</span> cachos
                  </p>
                  {setor.meses.map((mes) => (
                    <div
                      key={mes.mes}
                      className="p-2.5 rounded-md bg-muted/30 space-y-2"
                      data-testid={`mes-${setor.setorId}-${mes.mes}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{mes.mesLabel}</span>
                        <span className="font-mono text-xs tabular-nums">{mes.saldo.toLocaleString()} cachos</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Planejado</p>
                          <p className="font-mono tabular-nums">{mes.totalPlanejado.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Colhido</p>
                          <p className="font-mono tabular-nums text-sucesso">
                            {mes.totalColhido.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Perdido</p>
                          <p className="font-mono tabular-nums text-destructive">
                            {mes.totalPerdido.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </DataCard>
    </div>
  );
}
